import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { 
  AuthResponse, 
  User, 
  Customer, 
  Gateway,
  System,
  Product,
  CustomersResponse, 
  UsersResponse, 
  GatewaysResponse,
  SystemsResponse,
  ProductsResponse,
  DashboardStats, 
  Activity 
} from '../types';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      // Use relative API base in development to leverage CRA proxy and avoid CORS
      baseURL: process.env.REACT_APP_API_URL || '/api',
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response: AxiosResponse) => response.data,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          window.location.href = '/login';
        }
        return Promise.reject(error.response?.data || error.message);
      }
    );
  }

  // Authentication API
  auth = {
    login: (credentials: { loginId: string; password: string }): Promise<AuthResponse> =>
      this.api.post('/auth/login', credentials),
    
    getMe: (): Promise<User> =>
      this.api.get('/auth/me'),
    
    changePassword: (data: { currentPassword: string; newPassword: string }): Promise<{ message: string }> =>
      this.api.post('/auth/change-password', data),
  };

  // Customers API
  customers = {
    create: (customerData: any): Promise<{ message: string; customer: Customer }> =>
      this.api.post('/customers', customerData),
    
    getAll: (page = 1, limit = 10): Promise<CustomersResponse> =>
      this.api.get(`/customers?page=${page}&limit=${limit}`),
    
    getById: (id: string): Promise<Customer> =>
      this.api.get(`/customers/${id}`),
    
    update: (id: string, customerData: any): Promise<{ message: string; customer: Customer }> =>
      this.api.put(`/customers/${id}`, customerData),
    
    delete: (id: string): Promise<{ message: string }> =>
      this.api.delete(`/customers/${id}`),
    
    toggleStatus: (id: string): Promise<{ message: string; isActive: boolean }> =>
      this.api.put(`/customers/${id}/toggle-status`),
  };

  // Users API
  users = {
    create: (userData: any): Promise<{ message: string; user: User }> =>
      this.api.post('/users', userData),
    
    getAll: (page = 1, limit = 10): Promise<UsersResponse> =>
      this.api.get(`/users?page=${page}&limit=${limit}`),
    
    getById: (id: string): Promise<User> =>
      this.api.get(`/users/${id}`),
    
    update: (id: string, userData: any): Promise<{ message: string; user: User }> =>
      this.api.put(`/users/${id}`, userData),
    
    delete: (id: string): Promise<{ message: string }> =>
      this.api.delete(`/users/${id}`),
    
    toggleStatus: (id: string): Promise<{ message: string; isActive: boolean }> =>
      this.api.put(`/users/${id}/toggle-status`),
  };

  // Gateways API
  gateways = {
    create: (gatewayData: any): Promise<{ message: string; gateway: Gateway }> =>
      this.api.post('/gateways', gatewayData),
    
    getAll: (page = 1, limit = 10): Promise<GatewaysResponse> =>
      this.api.get(`/gateways?page=${page}&limit=${limit}`),
    
    getById: (id: string): Promise<Gateway> =>
      this.api.get(`/gateways/${id}`),
    
    update: (id: string, gatewayData: any): Promise<{ message: string; gateway: Gateway }> =>
      this.api.put(`/gateways/${id}`, gatewayData),
    
    delete: (id: string): Promise<{ message: string }> =>
      this.api.delete(`/gateways/${id}`),
    
    toggleStatus: (id: string): Promise<{ message: string; isActive: boolean }> =>
      this.api.put(`/gateways/${id}/toggle-status`),
    
    updateStatus: (id: string, status: 'online' | 'offline' | 'maintenance'): Promise<{ message: string; gateway: Gateway }> =>
      this.api.put(`/gateways/${id}/status`, { status }),
  };

  // Systems API
  systems = {
    create: (systemData: any): Promise<{ message: string; system: System }> =>
      this.api.post('/systems', systemData),
    
    getAll: (page = 1, limit = 10, search = '', customerId = ''): Promise<SystemsResponse> =>
      this.api.get(`/systems?page=${page}&limit=${limit}&search=${search}&customerId=${customerId}`),
    
    getById: (id: string): Promise<System> =>
      this.api.get(`/systems/${id}`),
    
    update: (id: string, systemData: any): Promise<{ message: string; system: System }> =>
      this.api.put(`/systems/${id}`, systemData),
    
    delete: (id: string): Promise<{ message: string }> =>
      this.api.delete(`/systems/${id}`),
    
    toggleStatus: (id: string): Promise<{ message: string; system: { id: string; isActive: boolean } }> =>
      this.api.patch(`/systems/${id}/toggle-status`),
    
    updateStatus: (id: string, status: 'online' | 'offline' | 'maintenance' | 'error'): Promise<{ message: string; system: { id: string; status: string } }> =>
      this.api.patch(`/systems/${id}/status`, { status }),

    addProduct: (systemId: string, productData: { productId: string; quantity?: number; metadata?: any }): Promise<{ message: string; system: System }> =>
      this.api.post(`/systems/${systemId}/products`, productData),

    removeProduct: (systemId: string, productId: string): Promise<{ message: string }> =>
      this.api.delete(`/systems/${systemId}/products/${productId}`),

    updateProduct: (
      systemId: string,
      productId: string,
      data: { quantity?: number; metadata?: any }
    ): Promise<{ message: string; system: System }> =>
      this.api.patch(`/systems/${systemId}/products/${productId}`, data),

    // system-level tanker entries endpoint removed; tanker data is product-scoped now
  };

  // Products API
  products = {
    create: (productData: any): Promise<{ message: string; product: Product }> =>
      this.api.post('/products', productData),
    
    getAll: (page = 1, limit = 10, search = ''): Promise<ProductsResponse> =>
      this.api.get(`/products?page=${page}&limit=${limit}&search=${search}`),
    
    getById: (id: string): Promise<Product> =>
      this.api.get(`/products/${id}`),
    
    update: (id: string, productData: any): Promise<{ message: string; product: Product }> =>
      this.api.put(`/products/${id}`, productData),
    
    delete: (id: string): Promise<{ message: string }> =>
      this.api.delete(`/products/${id}`),
    
    toggleStatus: (id: string): Promise<{ message: string; product: { id: string; isActive: boolean } }> =>
      this.api.patch(`/products/${id}/toggle-status`),
  };

  // Dashboard API
  dashboard = {
    getStats: (): Promise<DashboardStats> =>
      this.api.get('/dashboard/stats'),
    
    getRecentActivity: (): Promise<Activity[]> =>
      this.api.get('/dashboard/recent-activity'),
  };

  // Reports API
  reports = {
    getLatestForGateway: (gateway: string): Promise<any> =>
      this.api.get(`/reports/latest?gateway=${encodeURIComponent(gateway)}`),
    getSeries: (gateway: string, paramIndex: number, limit = 1000000000, from?: string, to?: string): Promise<{ field: string; points: Array<{ ts: string; value: number }>}> =>
      this.api.get(`/reports/series?gateway=${encodeURIComponent(gateway)}&paramIndex=${paramIndex}&limit=${limit}` + (from ? `&from=${encodeURIComponent(from)}` : '') + (to ? `&to=${encodeURIComponent(to)}` : '')),
  };
}

// Create and export API instance
const apiService = new ApiService();

export const authAPI = apiService.auth;
export const customersAPI = apiService.customers;
export const usersAPI = apiService.users;
export const gatewaysAPI = apiService.gateways;
export const systemsAPI = apiService.systems;
export const productsAPI = apiService.products;
export const dashboardAPI = apiService.dashboard;
export const reportsAPI = apiService.reports;

export default apiService;